import React, { useState, useEffect, useCallback } from 'react';
import { ref, set, get, update, onValue, off } from 'firebase/database';
import { db } from '../firebase';

const BattleScreen = ({ onBack, onGameStart, userName, setUserName, allQuestions }) => {
  const [mode, setMode] = useState(null);
  const [inputCode, setInputCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [roomInfo, setRoomInfo] = useState(null);
  const [roomData, setRoomData] = useState(null);
  const [playerCount, setPlayerCount] = useState(2);
  const [questionCount, setQuestionCount] = useState(20);
  const [selectedGenre, setSelectedGenre] = useState('ALL');
  const GENRE_LIST = [
  { key: 'ALL', label: '全範囲' },
  { key: 'A', label: 'A 消化管' },
  { key: 'B', label: 'B 肝臓' },
  { key: 'C', label: 'C 胆道' },
  { key: 'D', label: 'D 膵臓' },
  { key: 'E', label: 'E 循環器' },
  { key: 'F', label: 'F 呼吸器' },
  { key: 'G', label: 'G 腎臓' },
  { key: 'H', label: 'H 内分泌' },
  { key: 'I', label: 'I 血液' },
  { key: 'J', label: 'J 膠原病' },
  { key: 'KL', label: 'KL 神経' },
  { key: 'L', label: 'L 整形' },
  { key: 'M', label: 'M 感染症' },
  { key: 'O', label: 'O 産科' },
  { key: 'P', label: 'P 小児' },
  { key: 'PQ', label: 'PQ 精神' },
  { key: 'R', label: 'R 皮膚' },
  { key: 'S', label: 'S 眼科' },
  { key: 'T', label: 'T 耳鼻科' },
  { key: 'U', label: 'U 泌尿器' },
  { key: 'V', label: 'V 救急' },
  { key: 'W', label: 'W 公衆衛生' },
  { key: 'X', label: 'X 放射線' },
];

  const generateCode = () => Math.floor(1000 + Math.random() * 9000).toString();
  const generateId = () => Math.random().toString(36).slice(2, 8);

  // ★ onGameStart を useCallback でメモ化して無限ループを防ぐ
  const stableOnGameStart = useCallback(onGameStart, []);

  useEffect(() => {
    if (!roomInfo) return;
    const roomRef = ref(db, `battleRooms/${roomInfo.code}`);
    const unsub = onValue(roomRef, snap => {
      const data = snap.val();
      if (!data) return;
      setRoomData(data);
      if (data.status === 'playing') {
        stableOnGameStart(roomInfo);
      }
    });
    return () => off(roomRef, 'value', unsub);
  }, [roomInfo, stableOnGameStart]);

const handleCreate = async () => {
  if (!userName.trim()) return setError('名前を入力してください');
  setLoading(true);
  const code = generateCode();
  const hostId = generateId();

 const allData = (selectedGenre === 'ALL'
  ? Object.values(allQuestions).filter(q => Array.isArray(q)).flat()
  : (allQuestions[selectedGenre] || [])
).filter(q => q && q.s);
  for (let i = allData.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allData[i], allData[j]] = [allData[j], allData[i]];
  }

  const flattenQuestion = (q) => {
    if (!q.type) return q.q ?? '';
    if (q.type === 'indent') {
      return (q.q ? q.q + ' ' : '') + q.rows.map(r => r.text).join(' ');
    }
    if (q.type === 'list') {
      return (q.q ? q.q + ' ' : '') + q.items.map(item => `${item.label} 【${item.term}】：${item.desc}`).join(' ');
    }
    if (q.type === 'grouped') {
      return q.categories.flatMap(cat =>
        cat.groups.flatMap(g =>
          g.rows.map(r => [r.term, r.desc].filter(Boolean).join('：'))
        )
      ).join(' ');
    }
    return q.q ?? '';
  };

  const questions = allData.slice(0, questionCount).map(q => ({
    s: q.s ?? '',
    ch: q.ch ?? '',
    n: q.n ?? '',
    q: flattenQuestion(q),
  }));

  await set(ref(db, `battleRooms/${code}`), {
    host: hostId,
    status: 'lobby',
    playerCount,
    questionCount,
     genre: selectedGenre,  // ← 追加
    questions,
    currentIndex: 0,
    players: {
      [hostId]: { name: userName, isHost: true, score: 0, joinedAt: Date.now() }
    },
    answers: {},
    createdAt: Date.now(),
  });

  setRoomInfo({ code, myId: hostId, isHost: true });
  setLoading(false);
};
  const handleJoin = async () => {
    if (!userName.trim()) return setError('名前を入力してください');
    if (inputCode.length !== 4) return setError('4桁のコードを入力してください');
    setLoading(true);
    const snap = await get(ref(db, `battleRooms/${inputCode}`));
    if (!snap.exists()) {
      setError('ルームが見つかりません');
      setLoading(false);
      return;
    }
    const data = snap.val();
    if (data.status !== 'lobby') {
      setError('このルームはすでに開始しています');
      setLoading(false);
      return;
    }
    const currentPlayers = Object.keys(data.players || {}).length;
    if (currentPlayers >= data.playerCount) {
      setError('ルームが満員です');
      setLoading(false);
      return;
    }
    const myId = generateId();
    await update(ref(db, `battleRooms/${inputCode}/players`), {
      [myId]: { name: userName, isHost: false, score: 0, joinedAt: Date.now() }
    });
    setRoomInfo({ code: inputCode, myId, isHost: false });
    setLoading(false);
  };

  const handleStartGame = async () => {
    if (!roomInfo) return;
    const snap = await get(ref(db, `battleRooms/${roomInfo.code}`));
    const data = snap.val();
    const currentPlayers = Object.keys(data.players || {}).length;
    if (currentPlayers < data.playerCount) {
      return alert(`あと${data.playerCount - currentPlayers}人待っています`);
    }
    await update(ref(db, `battleRooms/${roomInfo.code}`), { status: 'playing' });
  };

  // ===== ロビー画面 =====
  if (roomInfo && roomData) {
    const players = Object.values(roomData.players || {}).sort((a, b) => a.joinedAt - b.joinedAt);
    const isFull = players.length >= roomData.playerCount;

    return (
      <div className="glass-card" style={{ maxWidth: '440px', width: '95%' }}>
        <h2 style={{ color: '#2c3e50', marginBottom: '6px' }}>対決ロビー</h2>

        <div style={{
          background: '#fef9e7', borderRadius: '16px', padding: '16px',
          marginBottom: '20px', width: '100%', boxSizing: 'border-box', textAlign: 'center'
        }}>
          <p style={{ color: '#7f8c8d', fontSize: '0.75rem', margin: '0 0 4px 0', letterSpacing: '0.1em' }}>ROOM CODE</p>
          <p style={{ color: '#e67e22', fontSize: '2.5rem', fontWeight: '900', margin: '0', letterSpacing: '0.3em' }}>
            {roomInfo.code}
          </p>
          <p style={{ color: '#95a5a6', fontSize: '0.75rem', margin: '4px 0 0 0' }}>
            {players.length} / {roomData.playerCount}人　{roomData.questionCount}問
          <p style={{ color: '#95a5a6', fontSize: '0.75rem', margin: '4px 0 0 0' }}>
  {players.length} / {roomData.playerCount}人　{roomData.questionCount}問　
  {GENRE_LIST.find(g => g.key === (roomData.genre || 'ALL'))?.label || '全範囲'}
</p>
          </p>
        </div>

        <div style={{ width: '100%', marginBottom: '20px' }}>
          <p style={{ color: '#7f8c8d', fontSize: '0.8rem', marginBottom: '8px' }}>参加者</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {players.map((p, i) => (
              <div key={i} style={{
                background: p.isHost ? '#fef9e7' : '#f8f9fa',
                border: p.isHost ? '2px solid #e67e22' : '2px solid #e1e8ed',
                borderRadius: '20px', padding: '6px 14px',
                fontSize: '0.85rem', fontWeight: 'bold',
                color: p.isHost ? '#e67e22' : '#2c3e50'
              }}>
                {p.isHost ? '👑 ' : '⚔️ '}{p.name}
              </div>
            ))}
            {Array.from({ length: roomData.playerCount - players.length }).map((_, i) => (
              <div key={`empty-${i}`} style={{
                background: '#f8f9fa', border: '2px dashed #e1e8ed',
                borderRadius: '20px', padding: '6px 14px',
                fontSize: '0.85rem', color: '#bdc3c7'
              }}>
                待機中...
              </div>
            ))}
          </div>
        </div>

        {roomInfo.isHost ? (
          <button className="playful-btn" onClick={handleStartGame}
            style={{ width: '100%', background: isFull ? '#e67e22' : '#bdc3c7', boxShadow: 'none' }}>
            {isFull ? '対決スタート！' : `あと${roomData.playerCount - players.length}人待っています`}
          </button>
        ) : (
          <p style={{ color: '#95a5a6', fontSize: '0.9rem' }}>
            ホストがゲームを開始するまで待ってください...
          </p>
        )}

        <button onClick={() => { setRoomInfo(null); setRoomData(null); setMode(null); }}
          style={{
            marginTop: '14px', background: 'none', border: '2px solid #e74c3c',
            color: '#e74c3c', cursor: 'pointer', fontSize: '0.85rem',
            padding: '8px 20px', borderRadius: '20px', fontWeight: 'bold'
          }}>
          ルームを退出する
        </button>
      </div>
    );
  }

  // ===== ルーム作成・参加画面 =====
  return (
    <div className="glass-card" style={{ maxWidth: '440px', width: '95%' }}>
      <h2 style={{ color: '#2c3e50', marginBottom: '6px' }}>クイズ対決</h2>
      <p style={{ color: '#95a5a6', fontSize: '0.85rem', marginBottom: '24px' }}>
        タイピングで早押し対決！全問終了後に合計点で勝負
      </p>

      <div style={{ marginBottom: '16px', width: '100%' }}>
        <label style={{ fontSize: '0.8rem', color: '#7f8c8d', display: 'block', marginBottom: '6px' }}>
          あなたの名前
        </label>
        <input type="text" value={userName} onChange={e => setUserName(e.target.value)}
          placeholder="名前を入力" maxLength={10}
          style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '2px solid #e1e8ed', fontSize: '1rem', boxSizing: 'border-box', outline: 'none' }}
        />
      </div>

      {error && <p style={{ color: '#e74c3c', fontSize: '0.8rem', marginBottom: '10px' }}>{error}</p>}

      {!mode && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
          <button className="playful-btn" style={{ background: '#e67e22' }}
            onClick={() => { setMode('create'); setError(''); }}>
            ルームを作る
          </button>
          <button className="playful-btn" onClick={() => { setMode('join'); setError(''); }}>
            ルームに参加する
          </button>
        </div>
      )}

      {mode === 'create' && (
        <div style={{ width: '100%' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '0.8rem', color: '#7f8c8d', display: 'block', marginBottom: '8px' }}>人数</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => setPlayerCount(n)} style={{
                  flex: 1, padding: '10px', borderRadius: '10px', border: '2px solid',
                  borderColor: playerCount === n ? '#e67e22' : '#e1e8ed',
                  background: playerCount === n ? '#fef9e7' : 'white',
                  color: playerCount === n ? '#e67e22' : '#7f8c8d',
                  fontWeight: 'bold', cursor: 'pointer', fontSize: '1rem'
                }}>
                  {n}人
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
  <label style={{ fontSize: '0.8rem', color: '#7f8c8d', display: 'block', marginBottom: '8px' }}>ジャンル</label>
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
    {GENRE_LIST.map(g => (
      <button key={g.key} onClick={() => setSelectedGenre(g.key)} style={{
        padding: '6px 12px', borderRadius: '20px', border: '2px solid',
        borderColor: selectedGenre === g.key ? '#e67e22' : '#e1e8ed',
        background: selectedGenre === g.key ? '#fef9e7' : 'white',
        color: selectedGenre === g.key ? '#e67e22' : '#7f8c8d',
        fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem'
      }}>
        {g.label}
      </button>
    ))}
  </div>
</div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '0.8rem', color: '#7f8c8d', display: 'block', marginBottom: '8px' }}>問題数</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[10, 20, 30, 50].map(n => (
                <button key={n} onClick={() => setQuestionCount(n)} style={{
                  flex: 1, padding: '10px', borderRadius: '10px', border: '2px solid',
                  borderColor: questionCount === n ? '#e67e22' : '#e1e8ed',
                  background: questionCount === n ? '#fef9e7' : 'white',
                  color: questionCount === n ? '#e67e22' : '#7f8c8d',
                  fontWeight: 'bold', cursor: 'pointer', fontSize: '0.9rem'
                }}>
                  {n}問
                </button>
              ))}
            </div>
          </div>

          <button className="playful-btn" style={{ background: '#e67e22', width: '100%' }}
            onClick={handleCreate} disabled={loading}>
            {loading ? '作成中...' : 'ルームを作成'}
          </button>
          <button onClick={() => setMode(null)} style={{
            marginTop: '10px', background: 'none', border: 'none',
            color: '#95a5a6', cursor: 'pointer', width: '100%'
          }}>
            ← 戻る
          </button>
        </div>
      )}

      {mode === 'join' && (
        <div style={{ width: '100%' }}>
          <label style={{ fontSize: '0.8rem', color: '#7f8c8d', display: 'block', marginBottom: '6px' }}>
            ルームコード（4桁）
          </label>
          <input type="text" value={inputCode}
            onChange={e => setInputCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="1234"
            style={{
              width: '100%', padding: '12px', borderRadius: '12px', border: '2px solid #e1e8ed',
              fontSize: '1.8rem', textAlign: 'center', letterSpacing: '0.3em',
              boxSizing: 'border-box', outline: 'none', marginBottom: '12px'
            }}
          />
          <button className="playful-btn" style={{ background: '#e67e22', width: '100%' }}
            onClick={handleJoin} disabled={loading}>
            {loading ? '参加中...' : '参加する'}
          </button>
          <button onClick={() => setMode(null)} style={{
            marginTop: '10px', background: 'none', border: 'none',
            color: '#95a5a6', cursor: 'pointer', width: '100%'
          }}>
            ← 戻る
          </button>
        </div>
      )}

      {/* ★ タイトルに戻るボタンをボタンっぽく */}
      <button onClick={onBack} style={{
        marginTop: '20px', background: 'white', cursor: 'pointer',
        border: '2px solid #e1e8ed', borderRadius: '50px',
        padding: '10px 28px', color: '#7f8c8d', fontWeight: 'bold',
        fontSize: '0.9rem', transition: 'all 0.2s ease'
      }}>
        ← タイトルに戻る
      </button>
    </div>
  );
};

export default BattleScreen;